import { Injectable, NgZone, computed, inject, signal } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Observable, from } from 'rxjs';
import { AccessLevel, UserInterface } from '../../interfaces/user.interface';
import {
  Permission,
  PERMISSION_ROLES,
  ROLE_MANAGEABLE_ROLES,
  TRIAL_GRANTER_MANAGEABLE_ROLES,
  TRIAL_GRANTER_PERMISSIONS,
} from '../../interfaces/permission.interface';
import { HttpClient } from '@angular/common/http';
import { UserService } from '../user/user.service';
import { Router } from '@angular/router';
import { ToastService } from '../toast/toast.service';
import { ApiUser } from '../../api-interfaces/api-user.interface';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { ulid } from 'ulid';
import { FeatureFlagService } from '../feature-flag/feature-flag.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  http = inject(HttpClient);
  firebaseAuth = inject(AngularFireAuth);
  firestore = inject(AngularFirestore);
  userService = inject(UserService);
  router = inject(Router);
  toastService = inject(ToastService);
  ngZone = inject(NgZone);
  featureFlagService = inject(FeatureFlagService);

  user$ = this.firebaseAuth.user;

  currentUserSig = signal<UserInterface | null | undefined>(undefined);
  currentCompanyId = computed(() => this.currentUserSig()?.companyId);
  currentCompanyName = computed(() => this.currentUserSig()?.companyName);
  currentFirstName = computed(() => this.currentUserSig()?.firstName);
  currentLastName = computed(() => this.currentUserSig()?.lastName);
  currentFullName = computed(() => this.currentUserSig()?.fullName);
  currentEmail = computed(() => this.currentUserSig()?.email);
  currentUserId = computed(() => this.currentUserSig()?.id);
  currentAccessLevel = computed(() => this.currentUserSig()?.accessLevel);

  isOperator = computed(() => this.currentAccessLevel() === AccessLevel.OPERATOR);
  isCargoStuffingOnly = computed(() => this.currentAccessLevel() === AccessLevel.CARGO_STUFFING);
  isAdminSuper = computed(() => this.currentAccessLevel() === AccessLevel.ADMIN_SUPER);
  canGrantTrialAccess = computed(() => !!this.currentUserSig()?.canGrantTrialAccess);
  /**
   * Fim do teste que se aplica ao usuário atual (espelha o TrialGuard do BE):
   * admin_super e representantes nunca expiram.
   */
  trialExpiresAt = computed(() => {
    const user = this.currentUserSig();
    if (!user?.trialExpiresAt || this.isAdminSuper() || user.canGrantTrialAccess) return null;
    return new Date(user.trialExpiresAt);
  });

  // Função (não computed): depende do relógio, reavaliada a cada navegação.
  isTrialExpired(): boolean {
    const expiresAt = this.trialExpiresAt();
    return !!expiresAt && expiresAt.getTime() <= Date.now();
  }

  /** Teste vencido detectado pela API (403 TRIAL_EXPIRED) no meio da sessão. */
  handleTrialExpired(): void {
    const user = this.currentUserSig();
    if (user && !this.isTrialExpired()) {
      this.patchUser({ trialExpiresAt: new Date().toISOString() });
    }
    this.router.navigate(['/trial-expired']);
  }

  patchUser(newData: Partial<UserInterface>): void {
    this.currentUserSig.set({
      ...this.currentUserSig()!,
      ...newData!
    });
  }

  login(email: string, password: string): Observable<void> {
    const promise = this.firebaseAuth.signInWithEmailAndPassword(
      email, 
      password
    ).then((res) => {
      const firebaseId = res.user?.uid;
      return this.setUserDataFromFirebaseId(firebaseId!, false);
    });
    return from(promise);
  }

  logout(): Observable<void> {
    this.featureFlagService.reset();
    const promise = this.firebaseAuth.signOut();
    return from(promise);
  }

  logoutAndRedirect(): void {
    this.logout().subscribe(() => {
      this.router.navigate(['/auth/login']);
    });
  }

  sendPasswordResetEmail(email: string): Observable<void> {
    const promise = this.firebaseAuth.sendPasswordResetEmail(email);
    return from(promise);
  }

  validatePasswordResetCode(code: string): Promise<string> {
    return this.firebaseAuth.verifyPasswordResetCode(code);
  }

  resetPassword(code: string, newPassword: string): Observable<void> {
    const promise = this.firebaseAuth.confirmPasswordReset(code, newPassword);
    return from(promise);
  }

  /**
   * Autorização por capability: a política inteira vive em PERMISSION_ROLES.
   * Role ou permission desconhecida => false (fail-safe p/ roles legadas).
   */
  userHasAccess(permission: Permission | Permission[]): boolean {
    const role = this.currentAccessLevel();
    if (!role) return false;
    // Aceita lista (any-of): usado por rotas compartilhadas por mais de um
    // item de menu — ex.: a Execução (remessa/carga) mora na mesma rota, então
    // o guard libera quem tem EXEC_REMESSA OU EXEC_CARGA.
    const permissions = Array.isArray(permission) ? permission : [permission];
    return permissions.some((p) =>
      (PERMISSION_ROLES[p]?.includes(role) ?? false)
      || (this.canGrantTrialAccess() && TRIAL_GRANTER_PERMISSIONS.includes(p))
    );
  }

  /**
   * Perfis que o usuário atual pode criar/editar/excluir (espelha o BE). Na
   * área ADMIN, quem não é admin_super é o representante comercial operando
   * nas empresas de teste que cadastrou — vale a matriz do representante.
   */
  manageableRoles(adminMode: boolean): AccessLevel[] {
    const role = this.currentAccessLevel();
    if (!role) return [];
    if (adminMode && !this.isAdminSuper() && this.canGrantTrialAccess()) {
      return TRIAL_GRANTER_MANAGEABLE_ROLES;
    }
    return ROLE_MANAGEABLE_ROLES[role] ?? [];
  }

  /** Tela inicial de cada perfil (fallback único dos guards). */
  navigateToRoleHome(): void {
    if (this.userHasAccess(Permission.HOME)) {
      this.router.navigate(['/home']);
    } else if (this.isOperator()) {
      this.router.navigate(['/files/remittances-instructions'], {
        queryParams: { origin: 'remittances_optimization' },
      });
    } else if (this.isCargoStuffingOnly()) {
      this.router.navigate(['/calculations/cargo-stuffing']);
    } else {
      this.router.navigate(['/account/profile']);
    }
  }

  setApiUserAsCurrent(apiUser: ApiUser): void {
    // Alias TRANSITÓRIO para roles legadas (banco já migrado; rede de
    // segurança para ambientes ainda não migrados). Remover num release futuro.
    const legacyAlias: Record<string, AccessLevel> = {
      admin: AccessLevel.OTIMIZACAO,
      master: AccessLevel.OTIMIZACAO,
    };
    const role = (legacyAlias[apiUser.role as string] ?? apiUser.role) || null;

    this.currentUserSig.set({
      email: apiUser.email,
      companyId: apiUser.users_companies.company_id,
      companyName: apiUser.users_companies.company.name,
      firstName: apiUser.first_name,
      lastName: apiUser.last_name,
      fullName: `${apiUser.first_name} ${apiUser.last_name}`,
      id: apiUser.id!,
      accessLevel: role,
      role: role,
      canGrantTrialAccess: !!apiUser.can_grant_trial_access,
      trialExpiresAt: apiUser.users_companies.company.trial_expires_at ?? null,
    });

    // Identifica no PostHog SEMPRE que o usuário vira o atual (boot E login
    // SPA). Sem isso, num login sem reload as flags continuavam avaliadas como
    // usuário anônimo e módulos por conta (ex.: Estufamento) não apareciam
    // até um F5. O identify dispara o reload das flags; o nav recomputa via
    // flagsVersion.
    this.featureFlagService.identify(apiUser.id!, {
      role: apiUser.role,
      company_id: apiUser.users_companies.company_id,
      email: apiUser.email,
    });
  }

  async setUserDataFromFirebaseId(firebaseId: string, alreadyLoggedIn: boolean) {
    let user: ApiUser | null = null;

    try {
      user = (await this.userService.getUserByFirebaseId(firebaseId))!;
    } catch (error) {
      if (alreadyLoggedIn) {
        this.toastService.add({ severity: 'error', summary: 'Erro', detail: 'Erro ao buscar usuário.' });
        this.logoutAndRedirect();
      } else {
        throw error;
      }
      return;
    }

    if (!user?.users_companies?.company) {
      this.logoutAndRedirect();
      throw { code: 'auth/no-company' };
      return;
    }
    
    if (user) {
      this.setApiUserAsCurrent(user);
    }
  }

  async setUserSessionAndWatch() {
    await this.setCurrentUserSession();
    
    setTimeout(() => {
      this.watchUserSession();
    });
  }

  async setCurrentUserSession() {
    const currentUserId = this.currentUserId()!;

    let userSessionIds: any = JSON.parse(localStorage.getItem('userSessionIds') || '{}');
    if (!userSessionIds) {
      userSessionIds = {};
    }

    const sessionId = ulid();

    userSessionIds[currentUserId] = sessionId;
    localStorage.setItem('userSessionIds', JSON.stringify(userSessionIds));
    // AGUARDA o write confirmar: sem isso o watch podia começar antes e a
    // primeira emissão vinha com o sessionId de um login ANTERIOR (de outro
    // dispositivo/aba), derrubando o usuário no primeiro login à toa.
    await this.firestore.collection('userSessions').doc(currentUserId).set({ sessionId: sessionId });
  }

  async watchUserSession() {
    const currentUserId = this.currentUserId()!;

    const subscription = this.firestore.collection('userSessions').doc(currentUserId).valueChanges().subscribe((userSession: any) => {
      let userSessionIds: any = JSON.parse(localStorage.getItem('userSessionIds') || '{}');
      if (!userSessionIds) {
        userSessionIds = {};
      }

      const currentUserSessionId = userSessionIds[currentUserId];

      // Sem informação dos dois lados não há decisão a tomar (doc ainda não
      // sincronizado ou storage limpo) — só derruba com divergência REAL.
      if (!currentUserSessionId || !userSession?.sessionId) {
        return;
      }

      if (currentUserSessionId !== userSession.sessionId) {
        this.logoutAndRedirect();
        this.toastService.error('Sua conta foi acessada em outro dispositivo ou aba. Faça login novamente.');
        subscription.unsubscribe();
      }
    });
  }
}
